// Pure summary of the mafia team's night-kill picks — extracted from
// Night.tsx's TeamSummary so the logic can be unit-tested without a DOM.
//
// Mirrors the server resolver (server/src/room/night.ts, kill queue):
//   - Only NON-independent kills join the group tally. The hitman's one-time
//     kill is independent — the resolver queues it separately, so showing it
//     inside the group count would predict outcomes the resolver won't
//     produce (audit P2).
//   - A tie at the top is settled by the commander's own pick when it is one
//     of the leaders. The commander may be an unspent hitman: his kill stays
//     OUT of the tally, but his target still breaks the tie (resolver reads
//     the commander's kill action regardless of independence).

import type { PlayerView } from '../../shared/src/types.js';

export type TeamVote = NonNullable<PlayerView['mafiaKillVotes']>[number];

export type GroupOutcome =
  | { kind: 'none' }                                                 // no group votes yet
  | { kind: 'target'; targetName: string }                           // clear leader
  | { kind: 'tie-commander'; commanderName: string; targetName: string }
  | { kind: 'tie-none' };                                            // tie, commander didn't settle it

export interface TeamVoteSummary {
  group: GroupOutcome;
  /** Independent kills with a committed target (e.g. the hitman's shot) —
   *  rendered on their own line, never inside the group tally. */
  independent: Array<{ voterName: string; targetName: string }>;
}

export function summarizeTeamVotes(votes: TeamVote[]): TeamVoteSummary {
  const counts = new Map<string, { name: string; count: number }>();
  for (const v of votes) {
    if (v.independent) continue;
    if (!v.targetId || !v.targetName) continue;
    const cur = counts.get(v.targetId);
    if (cur) cur.count += 1;
    else counts.set(v.targetId, { name: v.targetName, count: 1 });
  }
  const independent = votes
    .filter((v) => v.independent && v.targetId && v.targetName)
    .map((v) => ({ voterName: v.voterName, targetName: v.targetName! }));

  if (counts.size === 0) return { group: { kind: 'none' }, independent };

  const top = Math.max(...[...counts.values()].map((v) => v.count));
  const topIds = [...counts.entries()]
    .filter(([, v]) => v.count === top)
    .map(([id]) => id);
  if (topIds.length === 1) {
    return {
      group: { kind: 'target', targetName: counts.get(topIds[0])!.name },
      independent,
    };
  }
  const commander = votes.find((v) => v.isCommander);
  if (
    commander &&
    commander.targetId &&
    commander.targetName &&
    topIds.includes(commander.targetId)
  ) {
    return {
      group: {
        kind: 'tie-commander',
        commanderName: commander.voterName,
        targetName: commander.targetName,
      },
      independent,
    };
  }
  return { group: { kind: 'tie-none' }, independent };
}
